import React, { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import { EXPORT_STAGES } from "./useReportExport";

// The progress dialog for a PDF export. Paired with useReportExport, which owns
// the phases; this file only draws them.
//
// Why a dialog rather than the inline 28px spinner these screens used to swap
// their button for: the server rasterises through PhantomJS and a long report
// genuinely takes ten to twenty seconds. A spinner that small, with no label,
// left people unsure whether the click had registered at all - and because the
// old chains had no .catch(), a failure left that spinner turning forever.

const STAGE_LABELS = {
  building: "Collecting records",
  rendering: "Rendering the PDF",
  downloading: "Preparing your download",
};

const useStyles = makeStyles((theme) => ({
  // Sweeping indeterminate bar. A determinate value would be a lie - the
  // server reports no progress, so this says "still working" honestly.
  "@keyframes sweep": {
    "0%": { left: "-40%" },
    "100%": { left: "100%" },
  },
  "@keyframes pulse": {
    "0%, 100%": { transform: "scale(1)", opacity: 1 },
    "50%": { transform: "scale(1.08)", opacity: 0.75 },
  },
  paper: { borderRadius: 14, minWidth: 340 },
  content: { padding: "28px 28px 12px", textAlign: "center" },
  icon: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    margin: "0 auto 16px",
  },
  iconRunning: {
    backgroundColor: "#fff4e8",
    color: "#ef7f1a",
    animation: "$pulse 1.6s ease-in-out infinite",
  },
  iconOk: { backgroundColor: "#e8f6ee", color: "#17864f" },
  iconErr: { backgroundColor: "#fdecea", color: "#c0392b" },
  title: { fontWeight: 700, marginBottom: 4 },
  sub: { color: theme.palette.text.secondary, fontSize: 13 },
  track: {
    position: "relative",
    height: 5,
    borderRadius: 4,
    backgroundColor: "#eef1f6",
    overflow: "hidden",
    margin: "18px 0 14px",
  },
  bar: {
    position: "absolute",
    top: 0,
    height: 5,
    width: "40%",
    borderRadius: 4,
    backgroundColor: "#ef7f1a",
    animation: "$sweep 1.15s ease-in-out infinite",
  },
  steps: { textAlign: "left", margin: "0 auto", display: "inline-block" },
  step: {
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    padding: "3px 0",
    color: theme.palette.text.secondary,
  },
  stepActive: { color: "#16202c", fontWeight: 600 },
  dot: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    marginRight: 10,
    flexShrink: 0,
    border: "2px solid #d8dee8",
    boxSizing: "border-box",
  },
  dotDone: { backgroundColor: "#17864f", borderColor: "#17864f" },
  dotActive: { borderColor: "#ef7f1a", backgroundColor: "#fff4e8" },
  elapsed: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
  errText: {
    fontSize: 13,
    color: "#c0392b",
    backgroundColor: "#fdecea",
    borderRadius: 8,
    padding: "10px 12px",
    marginTop: 4,
    wordBreak: "break-word",
  },
}));

const ReportProgress = ({
  status,
  stage,
  error,
  label = "Report",
  elapsed = 0,
  onRetry,
  onClose,
  successAutoCloseMs = 900,
}) => {
  const classes = useStyles();
  const open = status === "running" || status === "error" || status === "success";

  // A finished export should get out of the way on its own; the file has
  // already been handed to the browser by this point.
  useEffect(() => {
    if (status !== "success") return undefined;
    const t = setTimeout(() => onClose?.(), successAutoCloseMs);
    return () => clearTimeout(t);
  }, [status, onClose, successAutoCloseMs]);

  const activeIndex = EXPORT_STAGES.indexOf(stage);

  return (
    <Dialog
      open={open}
      // While it is working there is nothing to cancel server-side, so closing
      // would only hide the truth. Errors and successes are dismissible.
      // `onClose` being undefined during a run already blocks a backdrop
      // click from doing anything - `disableBackdropClick` was redundant on
      // top of that, and MUI deprecated the prop in favour of exactly this.
      onClose={status === "running" ? undefined : onClose}
      disableEscapeKeyDown={status === "running"}
      classes={{ paper: classes.paper }}
      TransitionComponent={Fade}
      aria-labelledby="report-progress-title"
    >
      <DialogContent className={classes.content}>
        {status === "running" && (
          <>
            <div className={`${classes.icon} ${classes.iconRunning}`} aria-hidden="true">
              <DescriptionOutlinedIcon style={{ fontSize: 32 }} />
            </div>
            <Typography id="report-progress-title" variant="h6" className={classes.title}>
              Generating {label}
            </Typography>
            <Typography className={classes.sub}>
              This can take a few moments for a large date range.
            </Typography>

            <div className={classes.track} role="progressbar" aria-label={`Generating ${label}`}>
              <div className={classes.bar} />
            </div>

            <div className={classes.steps}>
              {EXPORT_STAGES.map((s, i) => {
                const done = i < activeIndex;
                const active = i === activeIndex;
                return (
                  <div
                    key={s}
                    className={`${classes.step} ${active ? classes.stepActive : ""}`}
                  >
                    <span
                      className={`${classes.dot} ${done ? classes.dotDone : ""} ${
                        active ? classes.dotActive : ""
                      }`}
                    />
                    {STAGE_LABELS[s]}
                  </div>
                );
              })}
            </div>

            {/* Shown only once it has been slow enough to be worth reassuring
                about - a counter that appears instantly reads as a stopwatch on
                a fast export. */}
            {elapsed >= 3 && (
              <div className={classes.elapsed}>{elapsed}s elapsed</div>
            )}
          </>
        )}

        {status === "success" && (
          <>
            <div className={`${classes.icon} ${classes.iconOk}`} aria-hidden="true">
              <CheckCircleIcon style={{ fontSize: 34 }} />
            </div>
            <Typography id="report-progress-title" variant="h6" className={classes.title}>
              {label} ready
            </Typography>
            <Typography className={classes.sub}>Your download has started.</Typography>
          </>
        )}

        {status === "error" && (
          <>
            <div className={`${classes.icon} ${classes.iconErr}`} aria-hidden="true">
              <ErrorOutlineIcon style={{ fontSize: 34 }} />
            </div>
            <Typography id="report-progress-title" variant="h6" className={classes.title}>
              {label} failed
            </Typography>
            <Typography className={classes.sub}>
              Nothing was downloaded. You can try again.
            </Typography>
            {error && <div className={classes.errText}>{error}</div>}
          </>
        )}
      </DialogContent>

      {status !== "running" && (
        <DialogActions style={{ padding: "4px 20px 18px" }}>
          {status === "error" && onRetry && (
            <Button onClick={onRetry} startIcon={<RefreshIcon />} color="primary">
              Try again
            </Button>
          )}
          <Button onClick={onClose} variant="contained" color="primary" disableElevation>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ReportProgress;
